// ============================================================================
// ADR-0024 — Share capital & members (Corporate Facts)
// ============================================================================
// Four tabs: Share classes · Opening members (carried from last year; typed or
// pasted in the first year) · Movements in the year · Year-end members
// (computed, read-only). Lists with Add / Edit forms, like the related party
// register. Feeds the List of Shareholders and Share Transfers reports.

import React, { useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ActionMenu, type ActionMenuItem } from 'components/ui';
import { generateLocalId } from 'components/modals/modules/fsa/notes/panes/policy/SectionedNarrativeEditor';
import type {
  CorporateFactsData,
  MemberHolding,
  ShareClassEntry,
  ShareMovement,
  ShareMovementKind,
} from 'api/types/fsa/corporateFacts.types';
import { FactsField, FactsFormShell, factsFormGridStyle, parseFactsNumber } from './FactsForm';
import { KIND_LABEL, allMembers, closingMembers, kindGives, kindTakes, negativeHoldings, parsePastedMembers } from './factsMembers';

interface Props {
  data: CorporateFactsData;
  onChange: (patch: Partial<CorporateFactsData>) => void;
}

type Tab = 'classes' | 'opening' | 'movements' | 'closing';

const sharesText = (n: number) => n.toLocaleString('en-IN');
const moneyText = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dateText = (iso?: string) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');

const MembersEditor: React.FC<Props> = ({ data, onChange }) => {
  const classes = useMemo(() => data.share_classes ?? [], [data.share_classes]);
  const opening = useMemo(() => data.members_opening ?? [], [data.members_opening]);
  const movements = useMemo(() => data.share_movements ?? [], [data.share_movements]);
  const members = useMemo(() => allMembers(opening, movements), [opening, movements]);
  const memberName = (id?: string | null) => members.find(m => m.id === id)?.name ?? '—';
  const closing = useMemo(() => closingMembers(opening, movements), [opening, movements]);
  const negatives = negativeHoldings(closing);
  const classLabel = (id: string) => {
    const c = classes.find(x => x.local_id === id);
    return c?.label || (c?.class === 'PREFERENCE' ? 'Preference shares' : 'Equity shares');
  };

  const [tab, setTab] = useState<Tab>(classes.length === 0 ? 'classes' : 'opening');
  const [search, setSearch] = useState('');
  const [editingClass, setEditingClass] = useState<ShareClassEntry | null>(null);
  const [editingHolding, setEditingHolding] = useState<MemberHolding | null>(null);
  const [editingMovement, setEditingMovement] = useState<ShareMovement | null>(null);
  const [pasteOpen, setPasteOpen] = useState(false);

  // ── in-use checks (delete disabled while referenced) ────────────────────
  const classInUse = (id: string) => opening.some(h => h.class_id === id) || movements.some(m => m.class_id === id);
  const memberInUse = (id: string) => movements.some(m => m.from_id === id || m.to_id === id);

  const upsert = <T extends { local_id: string }>(list: T[], item: T) =>
    (list.some(x => x.local_id === item.local_id) ? list.map(x => (x.local_id === item.local_id ? item : x)) : [...list, item]);

  const q = search.trim().toLowerCase();
  const shownOpening = opening.filter(h => !q || h.name.toLowerCase().includes(q) || (h.folio ?? '').toLowerCase().includes(q));
  const shownMovements = [...movements]
    .sort((a, b) => (a.date ?? '9999').localeCompare(b.date ?? '9999'))
    .filter(m => !q || memberName(m.from_id).toLowerCase().includes(q)
      || (m.to_id ? memberName(m.to_id) : m.to_name ?? '').toLowerCase().includes(q)
      || KIND_LABEL[m.kind].toLowerCase().includes(q));

  const tabBtn = (id: Tab, label: string, count: number | null) => (
    <button type="button" role="tab" aria-selected={tab === id} style={segBtn(tab === id)} onClick={() => setTab(id)}>
      {label}{count != null && <span style={countPillStyle}>{count}</span>}
    </button>
  );

  return (
    <div style={cardStyle}>
      <div style={toolbarStyle}>
        <div style={segmentStyle} role="tablist" aria-label="Share capital and members">
          {tabBtn('classes', 'Share classes', classes.length)}
          {tabBtn('opening', 'Opening members', opening.length)}
          {tabBtn('movements', 'Movements', movements.length)}
          {tabBtn('closing', 'Year-end members', closing.length)}
        </div>
        {(tab === 'opening' || tab === 'movements') && (
          <input className="form-input" style={{ maxWidth: 220, fontSize: '0.8rem' }} placeholder="Search name or folio…"
            value={search} onChange={e => setSearch(e.target.value)} aria-label="Search" />
        )}
        <span style={{ flex: 1 }} />
        {tab === 'classes' && (
          <button className="btn btn-primary btn-sm"
            onClick={() => setEditingClass({ local_id: generateLocalId(), class: 'EQUITY', label: '', face_value: 10, paid_up: 10 })}>
            <IconSystem.add size={13} /> Add class
          </button>
        )}
        {tab === 'opening' && (
          <>
            <button className="btn btn-secondary btn-sm" disabled={classes.length === 0} onClick={() => setPasteOpen(true)}
              title={classes.length === 0 ? 'Add a share class first' : 'Paste rows copied from Excel: folio, name, shares, promoter (Y/N)'}>
              Paste from Excel
            </button>
            <button className="btn btn-primary btn-sm" disabled={classes.length === 0}
              onClick={() => setEditingHolding({ local_id: generateLocalId(), name: '', class_id: classes[0]?.local_id ?? '', shares: 0 })}>
              <IconSystem.add size={13} /> Add member
            </button>
          </>
        )}
        {tab === 'movements' && (
          <button className="btn btn-primary btn-sm" disabled={classes.length === 0}
            onClick={() => setEditingMovement({ local_id: generateLocalId(), kind: 'TRANSFER', class_id: classes[0]?.local_id ?? '', shares: 0 })}>
            <IconSystem.add size={13} /> Add movement
          </button>
        )}
      </div>

      {negatives.length > 0 && (
        <div style={warnBoxStyle}>
          More shares moved out than held: {negatives.map(h => `${h.name} (${sharesText(h.shares)} ${classLabel(h.class_id)})`).join(', ')}.
          Check the movements.
        </div>
      )}

      {/* ───── Share classes ───── */}
      {tab === 'classes' && (
        <>
          <div style={subtleStyle}>The classes of shares issued — each list and movement refers to one. Carried to next year.</div>
          {classes.length === 0 ? <div style={emptyRowStyle}>Add the company's share classes first (e.g. Equity shares of Rs. 10 each).</div> : (
            <table style={tableStyle}>
              <thead><tr>
                <th style={thStyle}>Class</th><th style={thStyle}>Printed as</th>
                <th style={thNumStyle}>Face value</th><th style={thNumStyle}>Paid-up per share</th><th style={{ ...thStyle, width: 44 }} aria-label="Actions" />
              </tr></thead>
              <tbody>
                {classes.map(c => {
                  const actions: ActionMenuItem[] = [
                    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={14} />, onClick: () => setEditingClass(c) },
                    { id: 'delete', label: classInUse(c.local_id) ? 'Delete (in use)' : 'Delete', icon: <IconSystem.trash size={14} />, variant: 'danger',
                      disabled: classInUse(c.local_id), onClick: () => onChange({ share_classes: classes.filter(x => x.local_id !== c.local_id) }) },
                  ];
                  return (
                    <tr key={c.local_id} style={rowStyle} onClick={() => setEditingClass(c)}>
                      <td style={tdStyle}><strong>{c.class === 'PREFERENCE' ? 'Preference' : 'Equity'}</strong></td>
                      <td style={tdStyle}>{classLabel(c.local_id)}</td>
                      <td style={tdNumStyle}>{moneyText(c.face_value)}</td>
                      <td style={tdNumStyle}>{moneyText(c.paid_up)}</td>
                      <td style={tdStyle} onClick={e => e.stopPropagation()}><ActionMenu id={`sc-${c.local_id}`} items={actions} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}

      {/* ───── Opening members ───── */}
      {tab === 'opening' && (
        <>
          <div style={subtleStyle}>
            Members at the start of the year — carried from last year's year-end list; in the first year, add or paste them.
            Change a holding during the year through Movements, not here.
          </div>
          {opening.length === 0 ? <div style={emptyRowStyle}>{classes.length === 0 ? 'Add a share class first.' : 'No members yet — add them or paste from Excel.'}</div> : (
            <HoldingTable
              rows={shownOpening.map(h => ({ id: h.local_id, folio: h.folio, name: h.name, cls: classLabel(h.class_id), shares: h.shares, promoter: !!h.promoter }))}
              total={opening.reduce((s, h) => s + (h.shares || 0), 0)}
              onEdit={id => setEditingHolding(opening.find(h => h.local_id === id) ?? null)}
              actions={id => [
                { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={14} />, onClick: () => setEditingHolding(opening.find(h => h.local_id === id) ?? null) },
                { id: 'delete', label: memberInUse(id) ? 'Delete (in a movement)' : 'Delete', icon: <IconSystem.trash size={14} />, variant: 'danger',
                  disabled: memberInUse(id), onClick: () => onChange({ members_opening: opening.filter(h => h.local_id !== id) }) },
              ]}
            />
          )}
        </>
      )}

      {/* ───── Movements ───── */}
      {tab === 'movements' && (
        <>
          <div style={subtleStyle}>Allotments, transfers, transmissions, buy-backs and forfeitures in the year — they make the year-end list and print in Share Transfers and Allotments.</div>
          {movements.length === 0 ? <div style={emptyRowStyle}>No movements in the year.</div> : (
            <table style={tableStyle}>
              <thead><tr>
                <th style={thStyle}>Date</th><th style={thStyle}>Nature</th><th style={thStyle}>From</th><th style={thStyle}>To</th>
                <th style={thStyle}>Class</th><th style={thNumStyle}>Shares</th><th style={thNumStyle}>Consideration</th>
                <th style={{ ...thStyle, width: 44 }} aria-label="Actions" />
              </tr></thead>
              <tbody>
                {shownMovements.map(m => {
                  const createdMember = !m.to_id && !!m.to_name?.trim();
                  const createdUsed = createdMember && memberInUse(m.local_id);
                  const actions: ActionMenuItem[] = [
                    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={14} />, onClick: () => setEditingMovement(m) },
                    { id: 'delete', label: createdUsed ? 'Delete (member used later)' : 'Delete', icon: <IconSystem.trash size={14} />, variant: 'danger',
                      disabled: createdUsed, onClick: () => onChange({ share_movements: movements.filter(x => x.local_id !== m.local_id) }) },
                  ];
                  return (
                    <tr key={m.local_id} style={rowStyle} onClick={() => setEditingMovement(m)}>
                      <td style={tdStyle}>{dateText(m.date)}</td>
                      <td style={tdStyle}><strong>{KIND_LABEL[m.kind]}</strong></td>
                      <td style={tdStyle}>{m.kind === 'ALLOTMENT' ? 'Company' : memberName(m.from_id)}</td>
                      <td style={tdStyle}>
                        {kindGives(m.kind) ? (m.to_id ? memberName(m.to_id) : m.to_name || '—') : 'Company'}
                        {createdMember && <span style={newPillStyle}>new</span>}
                      </td>
                      <td style={tdMutedStyle}>{classLabel(m.class_id)}</td>
                      <td style={tdNumStyle}>{sharesText(m.shares || 0)}</td>
                      <td style={tdNumStyle}>{moneyText(m.consideration)}</td>
                      <td style={tdStyle} onClick={e => e.stopPropagation()}><ActionMenu id={`sm-${m.local_id}`} items={actions} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}

      {/* ───── Year-end members (computed) ───── */}
      {tab === 'closing' && (
        <>
          <div style={subtleStyle}>Computed — opening members plus the year's movements. Printed in the List of Shareholders; next year's opening list.</div>
          {closing.length === 0 ? <div style={emptyRowStyle}>No members at the year end yet.</div> : (
            classes.map(c => {
              const holders = closing.filter(h => h.class_id === c.local_id);
              if (holders.length === 0) return null;
              const total = holders.reduce((s, h) => s + h.shares, 0);
              return (
                <div key={c.local_id}>
                  <div style={groupTitleStyle}>{classLabel(c.local_id)} · {sharesText(total)} shares
                    {c.paid_up != null ? ` · paid-up ${moneyText(total * c.paid_up)}` : ''}</div>
                  <table style={tableStyle}>
                    <thead><tr>
                      <th style={thStyle}>Folio</th><th style={thStyle}>Name</th><th style={thNumStyle}>Shares</th>
                      <th style={thNumStyle}>% of the class</th><th style={thStyle}>Promoter</th>
                    </tr></thead>
                    <tbody>
                      {holders.map(h => (
                        <tr key={h.id}>
                          <td style={tdMutedStyle}>{h.folio ?? '—'}</td>
                          <td style={tdStyle}><strong>{h.name}</strong></td>
                          <td style={tdNumStyle}>{sharesText(h.shares)}</td>
                          <td style={tdNumStyle}>{total ? `${((h.shares / total) * 100).toFixed(2)}%` : '—'}</td>
                          <td style={tdStyle}>{h.promoter ? 'Yes' : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })
          )}
        </>
      )}

      {editingClass && (
        <ClassForm cls={editingClass} isNew={!classes.some(c => c.local_id === editingClass.local_id)}
          onCancel={() => setEditingClass(null)}
          onSave={c => { onChange({ share_classes: upsert(classes, c) }); setEditingClass(null); }} />
      )}
      {editingHolding && (
        <HoldingForm holding={editingHolding} isNew={!opening.some(h => h.local_id === editingHolding.local_id)} classes={classes} classLabel={classLabel}
          onCancel={() => setEditingHolding(null)}
          onSave={h => { onChange({ members_opening: upsert(opening, h) }); setEditingHolding(null); }} />
      )}
      {editingMovement && (
        <MovementForm movement={editingMovement} isNew={!movements.some(m => m.local_id === editingMovement.local_id)}
          classes={classes} classLabel={classLabel}
          members={members.filter(m => m.id !== editingMovement.local_id)}
          onCancel={() => setEditingMovement(null)}
          onSave={m => { onChange({ share_movements: upsert(movements, m) }); setEditingMovement(null); }} />
      )}
      {pasteOpen && (
        <PasteForm classes={classes} classLabel={classLabel} onCancel={() => setPasteOpen(false)}
          onAdd={rows => { onChange({ members_opening: [...opening, ...rows] }); setPasteOpen(false); }} />
      )}
    </div>
  );
};

// ============================================================================
// Lists and forms
// ============================================================================

const HoldingTable: React.FC<{
  rows: Array<{ id: string; folio?: string; name: string; cls: string; shares: number; promoter: boolean }>;
  total: number;
  onEdit: (id: string) => void;
  actions: (id: string) => ActionMenuItem[];
}> = ({ rows, total, onEdit, actions }) => (
  <table style={tableStyle}>
    <thead><tr>
      <th style={thStyle}>Folio</th><th style={thStyle}>Name</th><th style={thStyle}>Class</th>
      <th style={thNumStyle}>Shares</th><th style={thStyle}>Promoter</th><th style={{ ...thStyle, width: 44 }} aria-label="Actions" />
    </tr></thead>
    <tbody>
      {rows.map(r => (
        <tr key={r.id} style={rowStyle} onClick={() => onEdit(r.id)}>
          <td style={tdMutedStyle}>{r.folio ?? '—'}</td>
          <td style={tdStyle}><strong>{r.name}</strong></td>
          <td style={tdMutedStyle}>{r.cls}</td>
          <td style={tdNumStyle}>{sharesText(r.shares)}</td>
          <td style={tdStyle}>{r.promoter ? 'Yes' : ''}</td>
          <td style={tdStyle} onClick={e => e.stopPropagation()}><ActionMenu id={`mh-${r.id}`} items={actions(r.id)} /></td>
        </tr>
      ))}
    </tbody>
    <tfoot><tr>
      <td style={tfootStyle} colSpan={3}>{rows.length} member{rows.length === 1 ? '' : 's'}</td>
      <td style={{ ...tfootStyle, textAlign: 'right' }}>{sharesText(total)}</td>
      <td style={tfootStyle} colSpan={2} />
    </tr></tfoot>
  </table>
);

const ClassForm: React.FC<{ cls: ShareClassEntry; isNew: boolean; onCancel: () => void; onSave: (c: ShareClassEntry) => void }> = ({ cls, isNew, onCancel, onSave }) => {
  const [c, setC] = useState(cls);
  const set = (patch: Partial<ShareClassEntry>) => setC(prev => ({ ...prev, ...patch }));
  const suggested = `${c.class === 'PREFERENCE' ? 'Preference' : 'Equity'} shares of Rs. ${c.face_value ?? '—'} each`;
  return (
    <FactsFormShell title={isNew ? 'Add share class' : 'Edit share class'} width={560} onCancel={onCancel}
      onSave={() => onSave({ ...c, label: c.label?.trim() || suggested })}>
      <div style={factsFormGridStyle}>
        <FactsField label="Class" htmlFor="sc-kind">
          <select id="sc-kind" className="form-input" value={c.class} onChange={e => set({ class: e.target.value as ShareClassEntry['class'] })}>
            <option value="EQUITY">Equity</option>
            <option value="PREFERENCE">Preference</option>
          </select>
        </FactsField>
        <FactsField label="Face value per share" htmlFor="sc-fv">
          <input id="sc-fv" className="form-input" inputMode="decimal" style={{ textAlign: 'right' }} value={c.face_value ?? ''}
            onChange={e => set({ face_value: parseFactsNumber(e.target.value) })} />
        </FactsField>
        <FactsField label="Paid-up per share" htmlFor="sc-pu">
          <input id="sc-pu" className="form-input" inputMode="decimal" style={{ textAlign: 'right' }} value={c.paid_up ?? ''}
            onChange={e => set({ paid_up: parseFactsNumber(e.target.value) })} />
        </FactsField>
        <FactsField label="Printed as" htmlFor="sc-label" hint={`Blank = “${suggested}”.`} span>
          <input id="sc-label" className="form-input" placeholder={suggested} value={c.label ?? ''} onChange={e => set({ label: e.target.value })} />
        </FactsField>
      </div>
    </FactsFormShell>
  );
};

const HoldingForm: React.FC<{
  holding: MemberHolding; isNew: boolean; classes: ShareClassEntry[]; classLabel: (id: string) => string;
  onCancel: () => void; onSave: (h: MemberHolding) => void;
}> = ({ holding, isNew, classes, classLabel, onCancel, onSave }) => {
  const [h, setH] = useState(holding);
  const set = (patch: Partial<MemberHolding>) => setH(prev => ({ ...prev, ...patch }));
  const ok = !!h.name.trim() && h.shares > 0 && !!h.class_id;
  return (
    <FactsFormShell title={isNew ? 'Add member (opening)' : `Edit — ${holding.name || 'member'}`} width={600} onCancel={onCancel}
      onSave={() => onSave({ ...h, name: h.name.trim() })} saveDisabled={!ok} saveHint={ok ? undefined : 'Enter the name, class and a number of shares.'}>
      <div style={factsFormGridStyle}>
        <FactsField label="Name" htmlFor="mh-name" span>
          <input id="mh-name" className="form-input" autoFocus value={h.name} onChange={e => set({ name: e.target.value })} />
        </FactsField>
        <FactsField label="Folio" htmlFor="mh-folio">
          <input id="mh-folio" className="form-input" value={h.folio ?? ''} onChange={e => set({ folio: e.target.value || undefined })} />
        </FactsField>
        <FactsField label="Class" htmlFor="mh-class">
          <select id="mh-class" className="form-input" value={h.class_id} onChange={e => set({ class_id: e.target.value })}>
            {classes.map(c => <option key={c.local_id} value={c.local_id}>{classLabel(c.local_id)}</option>)}
          </select>
        </FactsField>
        <FactsField label="Shares held at the start of the year" htmlFor="mh-shares">
          <input id="mh-shares" className="form-input" inputMode="numeric" style={{ textAlign: 'right' }} value={h.shares || ''}
            onChange={e => set({ shares: parseFactsNumber(e.target.value) ?? 0 })} />
        </FactsField>
        <label style={checkLabelStyle}>
          <input type="checkbox" checked={!!h.promoter} onChange={e => set({ promoter: e.target.checked })} /> Promoter
        </label>
      </div>
    </FactsFormShell>
  );
};

const MovementForm: React.FC<{
  movement: ShareMovement; isNew: boolean; classes: ShareClassEntry[]; classLabel: (id: string) => string;
  members: Array<{ id: string; name: string; folio?: string }>;
  onCancel: () => void; onSave: (m: ShareMovement) => void;
}> = ({ movement, isNew, classes, classLabel, members, onCancel, onSave }) => {
  const [m, setM] = useState(movement);
  const [toNew, setToNew] = useState(!movement.to_id && !!movement.to_name);
  const set = (patch: Partial<ShareMovement>) => setM(prev => ({ ...prev, ...patch }));
  const takes = kindTakes(m.kind);
  const gives = kindGives(m.kind);
  const toOk = !gives || (toNew ? !!m.to_name?.trim() : !!m.to_id);
  const ok = m.shares > 0 && !!m.class_id && (!takes || !!m.from_id) && toOk;
  const save = () => {
    const out: ShareMovement = { ...m };
    if (!takes) delete out.from_id;
    if (!gives || toNew) delete out.to_id;
    if (!gives || !toNew) { delete out.to_name; delete out.to_folio; delete out.to_promoter; }
    onSave(out);
  };
  return (
    <FactsFormShell title={isNew ? 'Add movement' : 'Edit movement'} width={680} onCancel={onCancel} onSave={save}
      saveDisabled={!ok} saveHint={ok ? undefined : 'Enter the shares, and who they move from / to.'}>
      <div style={factsFormGridStyle}>
        <FactsField label="Nature" htmlFor="sm-kind">
          <select id="sm-kind" className="form-input" value={m.kind} onChange={e => set({ kind: e.target.value as ShareMovementKind })}>
            {(Object.keys(KIND_LABEL) as ShareMovementKind[]).map(k => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
          </select>
        </FactsField>
        <FactsField label="Date" htmlFor="sm-date">
          <input id="sm-date" type="date" className="form-input" value={m.date ?? ''} onChange={e => set({ date: e.target.value || undefined })} />
        </FactsField>
        <FactsField label="Class" htmlFor="sm-class">
          <select id="sm-class" className="form-input" value={m.class_id} onChange={e => set({ class_id: e.target.value })}>
            {classes.map(c => <option key={c.local_id} value={c.local_id}>{classLabel(c.local_id)}</option>)}
          </select>
        </FactsField>
        <FactsField label="Number of shares" htmlFor="sm-shares">
          <input id="sm-shares" className="form-input" inputMode="numeric" style={{ textAlign: 'right' }} value={m.shares || ''}
            onChange={e => set({ shares: parseFactsNumber(e.target.value) ?? 0 })} />
        </FactsField>
        {takes && (
          <FactsField label="From (member)" htmlFor="sm-from">
            <select id="sm-from" className="form-input" value={m.from_id ?? ''} onChange={e => set({ from_id: e.target.value || null })}>
              <option value="">— choose —</option>
              {members.map(x => <option key={x.id} value={x.id}>{x.name}{x.folio ? ` (folio ${x.folio})` : ''}</option>)}
            </select>
          </FactsField>
        )}
        {gives && (
          <FactsField label="To" htmlFor="sm-to" span={!takes}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 6, fontSize: '0.8rem' }}>
              <label style={checkLabelStyle}><input type="radio" checked={!toNew} onChange={() => setToNew(false)} /> An existing member</label>
              <label style={checkLabelStyle}><input type="radio" checked={toNew} onChange={() => setToNew(true)} /> A new member</label>
            </div>
            {!toNew && (
              <select id="sm-to" className="form-input" value={m.to_id ?? ''} onChange={e => set({ to_id: e.target.value || null })}>
                <option value="">— choose —</option>
                {members.filter(x => x.id !== m.from_id).map(x => <option key={x.id} value={x.id}>{x.name}{x.folio ? ` (folio ${x.folio})` : ''}</option>)}
              </select>
            )}
          </FactsField>
        )}
        {gives && toNew && (
          <>
            <FactsField label="New member's name" htmlFor="sm-toname">
              <input id="sm-toname" className="form-input" value={m.to_name ?? ''} onChange={e => set({ to_name: e.target.value })} />
            </FactsField>
            <FactsField label="Folio" htmlFor="sm-tofolio">
              <input id="sm-tofolio" className="form-input" value={m.to_folio ?? ''} onChange={e => set({ to_folio: e.target.value || undefined })} />
            </FactsField>
            <label style={checkLabelStyle}>
              <input type="checkbox" checked={!!m.to_promoter} onChange={e => set({ to_promoter: e.target.checked })} /> Promoter
            </label>
          </>
        )}
        <FactsField label="Consideration (total)" htmlFor="sm-cons">
          <input id="sm-cons" className="form-input" inputMode="decimal" style={{ textAlign: 'right' }} value={m.consideration ?? ''}
            onChange={e => set({ consideration: parseFactsNumber(e.target.value) })} />
        </FactsField>
        <FactsField label="Remarks" htmlFor="sm-rem" span>
          <input id="sm-rem" className="form-input" value={m.remarks ?? ''} onChange={e => set({ remarks: e.target.value || undefined })} />
        </FactsField>
      </div>
    </FactsFormShell>
  );
};

const PasteForm: React.FC<{
  classes: ShareClassEntry[]; classLabel: (id: string) => string; onCancel: () => void; onAdd: (rows: MemberHolding[]) => void;
}> = ({ classes, classLabel, onCancel, onAdd }) => {
  const [text, setText] = useState('');
  const [classId, setClassId] = useState(classes[0]?.local_id ?? '');
  const parsed = useMemo(() => parsePastedMembers(text, classId, generateLocalId), [text, classId]);
  return (
    <FactsFormShell title="Paste members from Excel" width={640} onCancel={onCancel} onSave={() => onAdd(parsed.rows)}
      saveDisabled={parsed.rows.length === 0}
      saveHint={text.trim() ? `${parsed.rows.length} row(s) to add${parsed.skipped ? `, ${parsed.skipped} skipped (header or no name / shares)` : ''}.` : undefined}>
      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
        Copy the columns <strong>Folio · Name · Shares · Promoter (Y/N)</strong> from Excel and paste them below. A header row is skipped.
      </div>
      <FactsField label="Class for these rows" htmlFor="pm-class">
        <select id="pm-class" className="form-input" value={classId} onChange={e => setClassId(e.target.value)}>
          {classes.map(c => <option key={c.local_id} value={c.local_id}>{classLabel(c.local_id)}</option>)}
        </select>
      </FactsField>
      <textarea className="form-textarea" rows={10} value={text} onChange={e => setText(e.target.value)} aria-label="Pasted rows"
        placeholder={'1\tAnil Kumar\t6000\tY\n2\tBina Rao\t4000\tN'} style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} />
    </FactsFormShell>
  );
};

// ───── styles ──────────────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)', borderRadius: 8, padding: '0.75rem 1rem 1rem', marginBottom: '1rem',
  display: 'flex', flexDirection: 'column', gap: 8,
};
const toolbarStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
const segmentStyle: React.CSSProperties = {
  display: 'inline-flex', padding: 2, gap: 2, borderRadius: 8, background: 'var(--bg-primary)', border: '1px solid var(--border-color)', flexWrap: 'wrap',
};
const segBtn = (on: boolean): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', border: 'none', borderRadius: 6, cursor: 'pointer',
  fontSize: '0.82rem', fontWeight: 600,
  background: on ? 'var(--primary-color, #1d4ed8)' : 'transparent', color: on ? '#fff' : 'var(--text-secondary)',
});
const countPillStyle: React.CSSProperties = { fontSize: '0.68rem', fontWeight: 700, padding: '0 7px', borderRadius: 999, background: 'rgba(148,163,184,0.25)' };
const subtleStyle: React.CSSProperties = { fontSize: '0.75rem', color: 'var(--text-secondary)' };
const emptyRowStyle: React.CSSProperties = { fontSize: '0.8125rem', color: 'var(--text-secondary)', padding: '0.75rem', background: 'var(--bg-primary)', borderRadius: 6 };
const warnBoxStyle: React.CSSProperties = {
  fontSize: '0.78rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 10px',
};
const groupTitleStyle: React.CSSProperties = { fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', margin: '8px 0 4px' };
const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', background: 'var(--bg-primary)', borderRadius: 8, overflow: 'hidden', fontSize: '0.82rem',
};
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)',
  textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid var(--border-color)',
};
const thNumStyle: React.CSSProperties = { ...thStyle, textAlign: 'right' };
const rowStyle: React.CSSProperties = { cursor: 'pointer' };
const tdStyle: React.CSSProperties = { padding: '7px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-primary)', verticalAlign: 'middle' };
const tdMutedStyle: React.CSSProperties = { ...tdStyle, color: 'var(--text-secondary)' };
const tdNumStyle: React.CSSProperties = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const tfootStyle: React.CSSProperties = { padding: '7px 10px', fontWeight: 700, fontSize: '0.8rem', color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' };
const newPillStyle: React.CSSProperties = {
  marginLeft: 6, fontSize: '0.64rem', fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: '#dcfce7', color: '#166534',
};
const checkLabelStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: 'var(--text-primary)' };

export default MembersEditor;
